import { useEffect, useState } from "react";

import { Box, Divider, Grid } from "@mantine/core";

import { AuthorityConstants } from "constant";
import { Bank, BankSpecificResponse, BankSpecificStatus, BankCreditNoteResponse, NoteType, DocLanguage } from "types";
import { UserUtils } from "utils";

import { BankCommentsSection } from "./bank-comments-section";
import { DetailsSection } from "./details-section";
import { NotesSection } from "./notes-section";
import { PaymentPreparationSection } from "./payment-preparation-section";
import { RequestInfoSection } from "./request-info-section";

const SHOW_REQUEST_INFO_STATUSES: BankSpecificStatus[] = [
  'IN_PROGRESS', 'APPROVED', 'REJECTED', 'ADDITIONAL_INFO_REQUIRED',
  'SIGNING', 'CONTRACT_VERIFICATION', 'SIGNED_COMPLETE', 'SIGNING_SUSPENDED',
];

interface BankTabProps {
  bank: Bank;
  clientId: number;
  creditId: number;
  bankCreditRequestId: number;
  availableBanks: Bank[];
  bankSpecific?: BankSpecificResponse;
  bankStatus?: BankSpecificStatus;
  bankNotes: BankCreditNoteResponse[];
  processingNotes: BankCreditNoteResponse[];
  distanceFromOffice?: number;
  onlineAuthEligible?: boolean;
  clientVisaType?: "B" | "C";
  onAddNote: (note: string, noteType: NoteType) => void;
  onSaveRequestInfo: (requestNumber: string, collaboratorName: string) => void;
  onDocLanguageChange: (language: DocLanguage) => void;
  onMaxCreditChange: (value: number | undefined) => void;
  onMaxPeriodChange: (value: number | undefined) => void;
}

export const BankTab = ({
  bank,
  clientId,
  creditId,
  bankCreditRequestId,
  availableBanks,
  bankSpecific,
  bankStatus,
  bankNotes,
  processingNotes,
  distanceFromOffice,
  onlineAuthEligible,
  clientVisaType,
  onAddNote,
  onSaveRequestInfo,
  onDocLanguageChange,
  onMaxCreditChange,
  onMaxPeriodChange,
}: BankTabProps) => {
  const [requestNumber, setRequestNumber] = useState(bankSpecific?.requestNumber || "");
  const [collaboratorName, setCollaboratorName] = useState(bankSpecific?.collaboratorName || "");

  useEffect(() => {
    setRequestNumber(bankSpecific?.requestNumber || "");
    setCollaboratorName(bankSpecific?.collaboratorName || "");
  }, [bankSpecific?.requestNumber, bankSpecific?.collaboratorName]);

  const handleSaveOnBlur = () => {
    onSaveRequestInfo(requestNumber, collaboratorName);
  };

  const showRequestInfo = !!bankStatus && SHOW_REQUEST_INFO_STATUSES.includes(bankStatus);
  const showPaymentPrep = showRequestInfo && bankStatus !== 'IN_PROGRESS';
  const canViewPaymentPreparation = UserUtils.hasAnyAuthority([
    AuthorityConstants.ROLE_CON,
    AuthorityConstants.ROLE_CON_PAY,
    AuthorityConstants.ROLE_CON_ADJ,
    AuthorityConstants.ROLE_CON_ATT,
    AuthorityConstants.ROLE_CON_ATT_OTHER,
  ]);

  return (
    <Box>
      <Grid gutter="md">
        <Grid.Col span={4}>
          <DetailsSection
            bank={bank}
            bankCreditRequestId={bankCreditRequestId}
            docLanguage={bankSpecific?.docLanguage}
            onDocLanguageChange={onDocLanguageChange}
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <NotesSection type="BANK" notes={bankNotes} onAddNote={(note) => onAddNote(note, "BANK")} />
        </Grid.Col>

        <Grid.Col span={4}>
          <NotesSection type="PROCESSING" notes={processingNotes} onAddNote={(note) => onAddNote(note, "PROCESSING")} />
        </Grid.Col>
      </Grid>

      {showRequestInfo && (
        <Box
          p="md"
          mt="md"
          style={{
            backgroundColor: "rgba(241, 242, 244, 1)",
            borderRadius: 12,
            border: "1px solid rgba(50, 51, 67, 0.15)",
          }}
        >
          <RequestInfoSection
            requestNumber={requestNumber}
            collaboratorName={collaboratorName}
            onRequestNumberChange={setRequestNumber}
            onCollaboratorNameChange={setCollaboratorName}
            onRequestNumberBlur={handleSaveOnBlur}
            onCollaboratorNameBlur={handleSaveOnBlur}
          />

          <Divider my="md" />

          <BankCommentsSection bank={bank} bankCreditRequestId={bankCreditRequestId} />
        </Box>
      )}

      {showPaymentPrep && canViewPaymentPreparation && (
        <PaymentPreparationSection
          bank={bank}
          clientId={clientId}
          creditId={creditId}
          bankCreditRequestId={bankCreditRequestId}
          availableBanks={availableBanks}
          distanceFromOffice={distanceFromOffice}
          onlineAuthEligible={onlineAuthEligible}
          clientVisaType={clientVisaType}
          maxCreditAmount={bankSpecific?.maxCreditAmount}
          maxPaymentPeriod={bankSpecific?.maxPaymentPeriod}
          docLanguage={bankSpecific?.docLanguage}
          onMaxCreditChange={onMaxCreditChange}
          onMaxPeriodChange={onMaxPeriodChange}
          onDocLanguageChange={onDocLanguageChange}
          mt="md"
        />
      )}
    </Box>
  );
};
